import React, { useState } from "react";
import useInvoices from "../../hooks/useInvoices";
import Invoicing from "../../components/Invoicing/Invoicing";
import useUninvoicedItems from "../../hooks/useUninvoicedItems/useUninvoicedItems";
import MessageBox from "../../components/MessageBox";
import axios from "axios";
import useErrorHandler from "../../hooks/userErrorHandler";
import PageContainer from "../PageContainer";
import usePermissions from "../../hooks/usePermissions";
import usePersistentState from "../../hooks/usePersistenState";

export default () => {
  const [page, setPage] = usePersistentState("default-invoicing-page", 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const [uninvoicedPage, setUninvoicedPage] = useState<number>(1);
  const [uninvoicedRowsPerPage, setUninvoicedRowsPerPage] = useState<number>(
    10
  );

  const [invoicesCreated, setInvoicesCreated] = useState<number>(0);
  const [message, setMessage] = useState<string>("");
  const [newInvoiceId, setNewInvoiceId] = useState<any>();
  const [sortCriteria, setSortCriteria] = usePersistentState(
    "default-invoicing-sort-criteria",
    {
      sortBy: null,
      sortDirection: null
    }
  );
  const [uninvoicedSortCriteria, setUninvoicedSortCriteria] = useState<any>({
    sortBy: null,
    sortDirection: null
  });
  const setError = useErrorHandler();

  const {
    invoices,
    invoicesLoading,
    currentPage,
    pageSize,
    pageCount,
    totalCount
  } = useInvoices(
    page,
    rowsPerPage,
    sortCriteria.sortBy,
    sortCriteria.sortDirection,
    [
      page,
      rowsPerPage,
      invoicesCreated,
      sortCriteria.sortBy,
      sortCriteria.sortDirection
    ]
  );
  const {
    uninvoicedItems,
    uninvoicedItemsLoading,
    uninvoicedCurrentPage,
    uninvoicedTotalCount
  } = useUninvoicedItems(
    uninvoicedPage,
    uninvoicedRowsPerPage,
    uninvoicedSortCriteria.sortBy,
    uninvoicedSortCriteria.sortDirection,
    [
      uninvoicedPage,
      uninvoicedRowsPerPage,
      invoicesCreated,
      uninvoicedSortCriteria.sortBy,
      uninvoicedSortCriteria.sortDirection
    ]
  );
  const [
    openCurrencyUnavailableSnackBar,
    setOpenCurrencyUnavailableSnackBar
  ] = useState<boolean>(false);

  const permissions = usePermissions();

  function createInvoice(orderIds: number[]) {
    (async () => {
      try {
        setMessage("");
        const response = await axios.post("/api/Invoices", orderIds);
        setNewInvoiceId(response.data);
        setInvoicesCreated(c => c + 1);
        setMessage("Invoice created successfully");
      } catch (err) {
        if (
          err &&
          err.response &&
          err.response.status === 409 &&
          err.response.data.problems &&
          err.response.data.problems[0].problemKey ===
            "Order.Currency.Unavailable"
        ) {
          setOpenCurrencyUnavailableSnackBar(true);
        } else if (err.message === "Network Error") {
          document.location.href = "/";
        }
        setError("Unable to create the invoice. Please try again.", err);
      }
    })();
  }

  return (
    <div className="InvoicingContainer-container">
      <PageContainer title="Invoicing">
        <Invoicing
          isInvoicesLoading={invoicesLoading}
          isUninvoicedItemsLoading={uninvoicedItemsLoading}
          invoices={invoices}
          uninvoicedItems={uninvoicedItems}
          onCreateInvoice={createInvoice}
          paginationInfo={{
            currentPage: currentPage || (page ? page - 1 : 0),
            pageSize: pageSize,
            pageCount: pageCount,
            totalCount: totalCount
          }}
          onChangePageNumber={(page: number) => setPage(page)}
          onChangePageSize={(rowsPerPage: any) => setRowsPerPage(rowsPerPage)}
          uninvoicedPaginationInfo={{
            totalCount: uninvoicedTotalCount,
            currentPage: uninvoicedCurrentPage
          }}
          onChangeUninvoicedPageNumber={(page: number) =>
            setUninvoicedPage(page)
          }
          onChangeUninvoicedPageSize={(rowsPerPage: any) => {
            setUninvoicedRowsPerPage(rowsPerPage);
          }}
          onSort={(x: any) => setSortCriteria(x)}
          onUninvoicedSort={(x: any) => setUninvoicedSortCriteria(x)}
          permissions={permissions}
          sortCriteria={sortCriteria}
        />
        <MessageBox
          message={message}
          severity="success"
          onClose={() => setMessage("")}
          entityId={newInvoiceId}
        />
        {openCurrencyUnavailableSnackBar ? (
          <MessageBox
            severity="error"
            onClose={() => setOpenCurrencyUnavailableSnackBar(false)}
            message={
              "The currency/denominations selected are currently unavailable."
            }
          />
        ) : (
          <></>
        )}
      </PageContainer>
    </div>
  );
};
